import PropTypes from "prop-types";
import { Controller } from "react-hook-form";
import { SpeechToTextWrapper } from "./speechToText";

/**
 * Textarea with mute/unmute mic dictation (video-call style).
 * For floating-label fields (modals/forms), prefer InputAreaField with enableSpeechToText.
 */
const SpeechToTextArea = ({
  name,
  control,
  value: controlledValue,
  onChange: controlledOnChange,
  label = "",
  legend,
  placeholder = "",
  disabled = false,
  rows = 14,
  fieldsetClassName = "rounded-lg border border-gray-200 bg-gray-50/50 px-3 pb-3 min-w-0 flex flex-col",
  textareaClassName = "",
  showLegend = true,
  showListeningHint = true,
}) => {
  const legendText = legend ?? label;
  const minHeightClass =
    rows >= 14 ? "min-h-[18rem]" : rows >= 8 ? "min-h-[12rem]" : "min-h-[8rem]";

  const renderTextarea = (value, onChange) => (
    <SpeechToTextWrapper
      value={value}
      onChange={onChange}
      disabled={disabled}
      showListeningHint={showListeningHint}
    >
      {({ speechPaddingClass }) => (
        <textarea
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          className={`w-full flex-1 ${minHeightClass} rounded-md border border-gray-300 bg-white px-3 py-2 text-sm resize-y focus:outline-none focus:ring-1 focus:ring-[#217E90] disabled:bg-gray-100 disabled:cursor-not-allowed ${speechPaddingClass} ${textareaClassName}`}
        />
      )}
    </SpeechToTextWrapper>
  );

  const wrapFieldset = (body) => (
    <fieldset className={fieldsetClassName}>
      {showLegend && legendText ? (
        <legend className="px-1.5 text-xs font-semibold text-[#217E90] mb-1">
          {legendText}
        </legend>
      ) : null}
      <div className="pt-0.5 flex flex-col flex-1 min-h-0">{body}</div>
    </fieldset>
  );

  if (control && name) {
    return (
      <Controller
        name={name}
        control={control}
        render={({ field }) =>
          wrapFieldset(renderTextarea(field.value, field.onChange))
        }
      />
    );
  }

  return wrapFieldset(renderTextarea(controlledValue, controlledOnChange));
};

SpeechToTextArea.propTypes = {
  name: PropTypes.string,
  control: PropTypes.object,
  value: PropTypes.string,
  onChange: PropTypes.func,
  label: PropTypes.string,
  legend: PropTypes.string,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  rows: PropTypes.number,
  fieldsetClassName: PropTypes.string,
  textareaClassName: PropTypes.string,
  showLegend: PropTypes.bool,
  showListeningHint: PropTypes.bool,
};

export default SpeechToTextArea;
